/**
 * Centralized security utilities.
 *
 * Single, auditable home for the escaping/sanitization helpers that were
 * previously duplicated across feature folders. Keeping these in one place
 * gives static-analysis tooling a single sink to reason about and prevents
 * the implementations from drifting apart.
 */

/**
 * Characters that must be escaped before interpolating untrusted text into
 * HTML. Compiled once and reused.
 */
const HTML_ESCAPE_MAP: Record<string, string> = {
	"&": "&amp;",
	"<": "&lt;",
	">": "&gt;",
	'"': "&quot;",
	"'": "&#39;",
};

const HTML_ESCAPE_REGEX = /[&<>"']/g;

/** Quick check used to skip the replace pass when there is nothing to escape. */
const HAS_DANGEROUS_CHARS = /[&<>"']/;

/**
 * Escapes HTML entities to prevent XSS when injecting text into markup
 * (e.g. via `dangerouslySetInnerHTML` or chart-library tooltip formatters).
 *
 * Null/undefined become an empty string; numbers are stringified. The early
 * exit keeps the common (clean) case allocation-free.
 */
export const escapeHtml = (value: string | number | null | undefined): string => {
	if (value === null || value === undefined) return "";

	const str = String(value);

	if (!HAS_DANGEROUS_CHARS.test(str)) {
		return str;
	}

	return str.replace(HTML_ESCAPE_REGEX, (char) => HTML_ESCAPE_MAP[char]);
};

/**
 * Regex metacharacters to escape when treating arbitrary user text as a
 * literal inside a `RegExp`.
 *
 * Includes `-` so the result is also safe to embed inside a `[...]` character
 * class, where an unescaped `-` would otherwise create a character range.
 */
const REGEXP_ESCAPE_REGEX = /[-/\\^$*+?.()|[\]{}]/g;

/**
 * Escapes a string so it can be used as a literal within a regular expression.
 * Returns "" for null/undefined input.
 */
export const escapeRegExp = (str: string | null | undefined): string => {
	if (str === null || str === undefined) return "";
	return String(str).replace(REGEXP_ESCAPE_REGEX, "\\$&");
};

/** Upper bound on pattern length accepted by {@link safeRegExp}. */
export const SAFE_REGEXP_MAX_LENGTH = 1000;

/**
 * Builds a `RegExp` that matches `pattern` literally.
 *
 * Escapes all metacharacters and caps the input length to guard against
 * pathological/over-long inputs. Input longer than
 * {@link SAFE_REGEXP_MAX_LENGTH} is truncated before escaping.
 */
export const safeRegExp = (pattern: string | null | undefined, flags?: string): RegExp => {
	const capped = String(pattern ?? "").slice(0, SAFE_REGEXP_MAX_LENGTH);
	return new RegExp(escapeRegExp(capped), flags);
};

/** Options for {@link safeLiteralRegExp}. */
export interface SafeLiteralRegExpOptions {
	/** Wrap the literal in `\bâ€¦\b` so it only matches whole words. */
	wholeWord?: boolean;
	/** Wrap the literal in a capturing group (needed for `String.split`). */
	capture?: boolean;
}

/**
 * Builds a `RegExp` that matches `pattern` literally, with optional
 * word-boundary anchoring and capturing-group wrapping.
 *
 * Same core guarantees as {@link safeRegExp} (metacharacters escaped, length
 * capped); the options cover the common search/highlight/find-replace shapes
 * without callers having to hand-assemble the pattern. Defaults to a global,
 * case-insensitive match.
 */
export const safeLiteralRegExp = (
	pattern: string | null | undefined,
	{ capture = false, wholeWord = false }: SafeLiteralRegExpOptions = {},
	flags = "gi"
): RegExp => {
	const capped = String(pattern ?? "").slice(0, SAFE_REGEXP_MAX_LENGTH);
	const literal = capture ? `(${escapeRegExp(capped)})` : escapeRegExp(capped);
	return new RegExp(wholeWord ? `\\b${literal}\\b` : literal, flags);
};

/**
 * Builds a capturing `RegExp` that matches `pattern` literally â€” the common
 * "highlight the user's search term" case, where the capturing group lets the
 * result be passed to `String.prototype.split` to retain matched delimiters.
 * Thin wrapper over {@link safeLiteralRegExp}.
 */
export const safeHighlightRegExp = (pattern: string | null | undefined, flags = "gi"): RegExp =>
	safeLiteralRegExp(pattern, { capture: true }, flags);

/** Upper bound on the length of a single sanitized log token. */
const MAX_LOG_TOKEN_LENGTH = 2000;

/**
 * Control characters (incl. CR/LF/TAB) that enable log forging/injection.
 * Range covers the C0 control block (\x00-\x1F) and DEL (\x7F).
 */
// eslint-disable-next-line no-control-regex
const LOG_CONTROL_CHARS_REGEX = /[\x00-\x1F\x7F]/g;

/**
 * Sanitizes an arbitrary value before it is interpolated into a log message.
 *
 * Strips CR/LF and other control characters so attacker-influenced data cannot
 * forge additional log lines or inject terminal escape sequences, and caps the
 * length so an over-long value cannot flood the log. Always returns a string.
 */
export const sanitizeForLog = (value: unknown): string => {
	let str: string;
	if (value === null || value === undefined) {
		str = "";
	} else if (value instanceof Error) {
		str = value.message;
	} else if (typeof value === "object") {
		try {
			str = JSON.stringify(value);
		} catch {
			str = String(value);
		}
	} else {
		str = String(value);
	}

	return str.replace(LOG_CONTROL_CHARS_REGEX, " ").slice(0, MAX_LOG_TOKEN_LENGTH);
};

/**
 * Returns `true` only when `value` is a well-formed absolute URL using a safe
 * web protocol (`http:`/`https:`). Used to gate `fetch`/navigation against
 * SSRF and `javascript:`/`data:`/`file:` style scheme abuse.
 */
export const isSafeHttpUrl = (value: string | null | undefined): boolean => {
	if (!value || typeof value !== "string") return false;
	try {
		const { protocol } = new URL(value);
		return protocol === "https:" || protocol === "http:";
	} catch {
		return false;
	}
};

/**
 * Validates that `value` is a safe absolute http(s) URL and returns it
 * unchanged; throws otherwise. Use to guard the argument of `fetch`/`open`
 * when the URL originates from a server response or other untrusted source.
 */
export const assertSafeHttpUrl = (value: string | null | undefined): string => {
	if (!isSafeHttpUrl(value)) {
		throw new Error("Refusing to request an unsafe or malformed URL");
	}
	return value as string;
};
